import { z } from 'zod';
import { RESOLUTION_LADDER, resolutionSchema, type Resolution } from './domain.js';

/** Qué versión de HispaShare interesa y cuál no (D-21).
 *
 *  La ficha de una película ofrece diez o quince versiones que solo se distinguen por
 *  letra pequeña —`mkv (x265-EAC3)` frente a `avi (XviD-AC3)`—, y elegir bien exige
 *  compararlas una a una. Este módulo traduce esa letra pequeña a tres niveles por
 *  característica, que es lo que la ficha pinta en verde, ámbar y rojo.
 *
 *  Vive en el paquete compartido porque lo usan los dos lados: la API para validar y
 *  guardar las preferencias, y el navegador para pintar. Una sola definición de qué es
 *  «idónea» evita que la tabla y los ajustes digan cosas distintas.
 *
 *  Esto NO decide nada por su cuenta: no filtra la tabla ni descarta versiones. Quien
 *  elige sin que nadie mire es `services/watchlist/seleccion.ts`, y ese sigue con sus
 *  filtros duros. Aquí solo se colorea para que el ojo encuentre la buena. */

// ── Niveles ───────────────────────────────────────────────────────────────────

/** `desconocida` no es un cuarto grado de bondad: es «el sitio no lo dice». Un `avi`
 *  sin códec anunciado y un `mkv` con `x264` no se pueden pintar igual, y suponer un
 *  valor sería inventarse el dato que falta. */
export const nivelSchema = z.enum(['ideal', 'aceptable', 'rechazada', 'desconocida']);
export type Nivel = z.infer<typeof nivelSchema>;

const nivelElegibleSchema = z.enum(['ideal', 'aceptable', 'rechazada']);

/** Cuánto margen se le da a la banda de bitrate. Ver `MARGEN_TOLERANCIA`. */
export const toleranciaSchema = z.enum(['exigente', 'normal', 'tolerante']);
export type ToleranciaTamano = z.infer<typeof toleranciaSchema>;

// ── Vocabulario ───────────────────────────────────────────────────────────────

/** Familias de códec de vídeo. HispaShare escribe el códec a mano dentro del paréntesis
 *  del formato, y ahí cabe de todo: `x264`, `x265 10Bit Dolby Vision HDR`, `XviD`. Por eso
 *  se busca el patrón DENTRO del texto en vez de comparar la cadena entera. */
const FAMILIAS: { familia: string; patron: RegExp }[] = [
  { familia: 'h265', patron: /\b(?:x\.?265|h\.?265|hevc)\b/i },
  { familia: 'h264', patron: /\b(?:x\.?264|h\.?264|avc)\b/i },
  { familia: 'av1', patron: /\bav1\b/i },
  { familia: 'vp9', patron: /\bvp-?9\b/i },
  { familia: 'xvid', patron: /\b(?:xvid|divx|mpeg-?4)\b/i },
  { familia: 'mpeg2', patron: /\bmpeg-?2\b/i },
];

/** Cómo se llama cada familia para una persona. La ficha enseña el texto crudo del sitio;
 *  esto es para los ajustes, donde hay que elegir de una lista. */
export const NOMBRE_FAMILIA: Record<string, string> = {
  h264: 'H.264 (x264 / AVC)',
  h265: 'H.265 (x265 / HEVC)',
  av1: 'AV1',
  vp9: 'VP9',
  xvid: 'XviD / DivX',
  mpeg2: 'MPEG-2',
};

/** Los idiomas que sabe producir el parser de banderas de HispaShare
 *  (`adapters/hispashare/normalize.ts`), con su nombre para una persona.
 *
 *  Es una lista de presentación, no una validación: el vocabulario del sitio es abierto y
 *  las preferencias admiten cualquier código. Lo que NO es negociable es que `es` y
 *  `es-419` sean dos cosas distintas —el latino tiene su propia bandera— porque es
 *  justamente la diferencia entre «me vale» y «es lo que quiero». */
export const NOMBRE_IDIOMA: Record<string, string> = {
  es: 'castellano',
  'es-419': 'español latino',
  en: 'inglés',
  ca: 'catalán / valenciano',
  gl: 'gallego',
  eu: 'euskera',
  fr: 'francés',
  it: 'italiano',
  de: 'alemán',
  pt: 'portugués',
  ja: 'japonés',
};

/** De qué se habla en cada país, para deducir el idioma ORIGINAL de una película.
 *
 *  El único sitio donde HispaShare dice de dónde es una película es el paréntesis del
 *  título original (`Spider-Man (EE.UU.)`), así que este mapa es lo que convierte ese dato
 *  en «qué voz es la de verdad». Las claves van normalizadas —sin puntos, sin acentos y en
 *  minúsculas— porque el sitio escribe `EE.UU.` y cualquiera escribiría `EEUU`.
 *
 *  Es una aproximación declarada: Canadá y Bélgica tienen dos lenguas, y una coproducción
 *  puede rodarse en la del socio menor. Por eso un país que no esté aquí no se fuerza a
 *  nada —se queda sin idioma original— en vez de suponer inglés, que es el error que
 *  marcaría en ámbar media filmoteca. */
const IDIOMA_DE_PAIS: Record<string, string> = {
  espana: 'es', mexico: 'es', argentina: 'es', chile: 'es', colombia: 'es', peru: 'es',
  cuba: 'es', uruguay: 'es', venezuela: 'es',
  eeuu: 'en', 'estados unidos': 'en', 'reino unido': 'en', canada: 'en', australia: 'en',
  irlanda: 'en', 'nueva zelanda': 'en',
  francia: 'fr', italia: 'it', alemania: 'de', austria: 'de', suiza: 'de',
  portugal: 'pt', brasil: 'pt', japon: 'ja', china: 'zh', 'hong kong': 'zh', taiwan: 'zh',
  'corea del sur': 'ko', corea: 'ko', india: 'hi', rusia: 'ru', 'union sovietica': 'ru',
  suecia: 'sv', dinamarca: 'da', noruega: 'no', finlandia: 'fi', islandia: 'is',
  'paises bajos': 'nl', holanda: 'nl', belgica: 'nl', polonia: 'pl', grecia: 'el',
  turquia: 'tr', israel: 'he', iran: 'fa', hungria: 'hu', 'republica checa': 'cs',
  rumania: 'ro', marruecos: 'ar', egipto: 'ar',
};

function normalizarPais(p: string): string {
  return p
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[.]/g, '')
    .trim()
    .toLowerCase();
}

/** El idioma original de una película a partir de sus países.
 *
 *  Con una coproducción manda el PRIMERO que se reconozca: el sitio los lista por peso, y
 *  `EE.UU., Canadá` es una película en inglés. Devuelve `null` cuando no hay país o cuando
 *  ninguno está en el mapa, y ese `null` se propaga: no saber el original es un estado, no
 *  un motivo para castigar a la versión. */
export function idiomaOriginalDe(paises: string[] | null | undefined): string | null {
  for (const p of paises ?? []) {
    const idioma = IDIOMA_DE_PAIS[normalizarPais(p)];
    if (idioma) return idioma;
  }
  return null;
}

/** `x265 10Bit Dolby Vision HDR` → `h265`. Devuelve `null` si no se reconoce nada: es un
 *  vocabulario abierto y lo desconocido no se fuerza a ninguna familia. */
export function familiaCodec(texto: string | null | undefined): string | null {
  if (!texto) return null;
  return FAMILIAS.find((f) => f.patron.test(texto))?.familia ?? null;
}

/** ¿Es una versión estereoscópica? Se mira en la cabecera de calidad del sitio
 *  (`3D AA BDRip 1080p`, `3D SBS HD`, `3D HOU`), que es el único sitio donde lo dicen.
 *
 *  Un 3D no es una resolución peor ni un códec raro: es una película que en una pantalla
 *  normal se ve con las dos imágenes pegadas —lado a lado o una encima de otra— y no hay
 *  ajuste que la arregle. Por eso no es una preferencia: se rechaza siempre, y la elección
 *  automática la descarta antes de valorar nada. Se reconocen también las siglas de la
 *  disposición sin el `3D` delante (`SBS`, `HSBS`, `OU`, `HOU`, `TAB`), porque a veces van
 *  solas. */
export function esVersion3D(texto: string | null | undefined): boolean {
  if (!texto) return false;
  return /\b(?:3-?d|bd3d|h?-?sbs|h?-?ou|hou|h?-?tab|anaglif\w*|anaglyph)\b/i.test(texto);
}

/** El contenedor ya llega en minúsculas del parser, pero esto también se aplica a lo que
 *  se teclea en Ajustes, y ahí un `.MKV` con punto delante es de esperar. */
export function normalizarContenedor(texto: string | null | undefined): string | null {
  const limpio = texto?.trim().toLowerCase().replace(/^\./, '') ?? '';
  return limpio || null;
}

// ── Preferencias ──────────────────────────────────────────────────────────────

const codigoIdioma = z.string().trim().min(2).max(8);

export const esquemaPreferenciasVersion = z.object({
  /** La resolución que se busca. */
  resolucionIdeal: resolutionSchema,
  /** Qué merece una versión POR ENCIMA de la ideal. Por defecto `aceptable`: un 2160p
   *  sirve, pero ocupa el triple para una tele que no lo aprovecha. */
  resolucionPorEncima: nivelElegibleSchema,
  /** Y una por debajo. Por defecto `rechazada`. */
  resolucionPorDebajo: nivelElegibleSchema,
  /** Contenedores que se quieren y los que se toleran. Lo que no esté en ninguna de las
   *  dos listas queda rechazado: es lo que hace que basten dos listas y no tres. */
  contenedoresIdeales: z.array(z.string().trim().min(1).max(12)).max(10),
  contenedoresAceptables: z.array(z.string().trim().min(1).max(12)).max(10),
  /** Familias de códec de vídeo, en las claves de `NOMBRE_FAMILIA`. */
  codecsIdeales: z.array(z.string().trim().min(1).max(12)).max(10),
  codecsAceptables: z.array(z.string().trim().min(1).max(12)).max(10),
  /** Los idiomas que entiendes, en los códigos que produce el parser de banderas (`es`,
   *  `es-419`, `en`…). Basta con que esté uno de los ideales: es lo que hace que «tiene
   *  castellano» gane a «además trae inglés».
   *
   *  La misma lista juzga el AUDIO y los SUBTÍTULOS —la pregunta es la misma, «puedo
   *  seguirla»— y por eso no hay dos pares de listas que mantener de acuerdo. Lo que cambia
   *  entre las dos columnas es la consecuencia: sin audio que entiendas la versión no vale,
   *  y sin subtítulos, como mucho, es mejorable. */
  audioIdeal: z.array(codigoIdioma).max(10),
  audioAceptable: z.array(codigoIdioma).max(10),
  /** Si además hay que tener la película **en su idioma original**, en audio y en
   *  subtítulos. Con esto puesto,
   *  una versión que solo trae el doblaje se queda en ámbar: se entiende, pero no es como
   *  se rodó. Una película hispanohablante no pierde nada, porque ahí el castellano ES el
   *  original. Ver `juzgarAudio` para qué se hace cuando la ficha no dice el país. */
  exigirIdiomaOriginal: z.boolean(),
  /** Cuánto se aprieta la banda de bitrate de `BANDA_BITRATE`. La banda ya viene medida
   *  sobre versiones reales; esto solo la ensancha o la estrecha entera. */
  toleranciaTamano: toleranciaSchema,
});

export type PreferenciasVersion = z.infer<typeof esquemaPreferenciasVersion>;

/** Los valores de partida. Se pueden cambiar enteros desde Ajustes. */
export const PREFERENCIAS_VERSION_POR_DEFECTO: PreferenciasVersion = {
  resolucionIdeal: '1080p',
  resolucionPorEncima: 'aceptable',
  resolucionPorDebajo: 'rechazada',
  contenedoresIdeales: ['mkv'],
  contenedoresAceptables: [],
  codecsIdeales: ['h264'],
  codecsAceptables: ['h265'],
  audioIdeal: ['es'],
  // El latino es español, pero no es el castellano: sirve si no hay otra cosa.
  audioAceptable: ['es-419'],
  exigirIdiomaOriginal: true,
  toleranciaTamano: 'normal',
};

// ── Valoración ────────────────────────────────────────────────────────────────

export interface Juicio {
  nivel: Nivel;
  /** Por qué, en una frase. Va al tooltip de la celda: un color sin motivo obliga a
   *  recordar los ajustes de memoria. */
  motivo: string;
}

export interface Valoracion {
  resolucion: Juicio;
  contenedor: Juicio;
  codec: Juicio;
  audio: Juicio;
  subtitulos: Juicio;
  tamano: Juicio;
  /** El peor de los cinco. Una versión con todo verde salvo el idioma no vale. */
  conjunto: Nivel;
  /** Para ordenar. Ver `PESOS`. */
  puntos: number;
}

/** Lo mínimo que hace falta para valorar. Es la forma que tiene un release, pero declarado
 *  aparte para que un candidato construido a mano —los tests, una previsualización— no
 *  tenga que rellenar la ficha entera. */
export interface VersionValorable {
  resolution: string | null;
  /** La cabecera de calidad tal como la escribe el sitio (`3D AA BDRip 1080p`). De aquí
   *  sale la resolución en el adaptador, pero se conserva porque también dice si la
   *  versión es 3D, y eso no cabe en ningún otro campo. */
  qualityRaw?: string | null;
  container: string | null;
  videoCodec: string | null;
  audioLangs: string[];
  /** Subtítulos de verdad. Los **forzados** no entran: solo traducen lo que en la propia
   *  película ya suena en otro idioma, y contarlos prometería una subtitulación que no
   *  existe. Por eso viajan aparte también aquí. */
  subtitleLangs?: string[];
  /** El sitio dice que hay subtítulos pero no de qué idioma (bandera `subs`). */
  subsSinEspecificar?: boolean;
  /** El nombre entra porque a veces el 3D solo va ahí: «Jurassic Park … 3D SBS.mkv» con
   *  «BDrip 1080p» en la cabecera, y se colocó en la biblioteca. */
  files: { kind: string; sizeBytes: number | null; fileName?: string | null }[];
}

/** ¿Es 3D esta versión? La cabecera de calidad o el nombre de cualquiera de sus vídeos:
 *  quien sube un release no siempre lo marca en los dos sitios. */
export function versionEs3D(v: VersionValorable): boolean {
  return (
    esVersion3D(v.qualityRaw) ||
    v.files.some((f) => f.kind === 'video' && esVersion3D(f.fileName))
  );
}

/** Cuánto pesa cada característica al ordenar.
 *
 *  El audio va primero porque es el único requisito que no se negocia: una película sin
 *  castellano no se ve, por muy bien empaquetada que esté. El tamaño y los subtítulos van
 *  últimos por motivos distintos: el tamaño es el más indirecto —dice cuánto se apartó de
 *  lo normal, no si se ve bien— y unos subtítulos que falten se descargan aparte. */
const PESOS = { audio: 5, resolucion: 4, contenedor: 3, codec: 2, subtitulos: 1, tamano: 1 } as const;

/** Una rechazada RESTA en vez de valer cero: así una versión con una sola característica
 *  imposible cae por debajo de otra a la que solo le falta el dato, que es el orden en el
 *  que se quieren mirar. */
const VALOR: Record<Nivel, number> = { ideal: 2, aceptable: 1, desconocida: 0, rechazada: -2 };

function indiceResolucion(r: string | null): number {
  return r && (RESOLUTION_LADDER as readonly string[]).includes(r)
    ? RESOLUTION_LADDER.indexOf(r as Resolution)
    : -1;
}

/** La resolución, que es también donde se cuelga el 3D: va en la misma cabecera del sitio
 *  y la ficha ya enseña ese texto debajo del `1080p`. Un 3D se rechaza antes de mirar nada
 *  más, y sin preferencia que lo afloje. */
function juzgarResolucion(v: VersionValorable, p: PreferenciasVersion): Juicio {
  if (versionEs3D(v)) {
    const donde = esVersion3D(v.qualityRaw)
      ? v.qualityRaw!.trim()
      : v.files.find((f) => f.kind === 'video' && esVersion3D(f.fileName))!.fileName!;
    return {
      nivel: 'rechazada',
      motivo: `Es una versión 3D (${donde}): en una pantalla normal se ven las dos imágenes a la vez`,
    };
  }
  const r = v.resolution;
  const suya = indiceResolucion(r);
  if (suya < 0) {
    return { nivel: 'desconocida', motivo: 'El sitio no dice la resolución de esta versión' };
  }
  const ideal = indiceResolucion(p.resolucionIdeal);
  if (suya === ideal) return { nivel: 'ideal', motivo: `${r} es justo la resolución que buscas` };

  const porEncima = suya > ideal;
  const nivel = porEncima ? p.resolucionPorEncima : p.resolucionPorDebajo;
  const relacion = porEncima ? 'por encima de' : 'por debajo de';
  const explicacion =
    nivel === 'ideal'
      ? 'y te vale igual'
      : nivel === 'aceptable'
        ? 'pero te puedes conformar'
        : 'y no te vale';
  return { nivel, motivo: `${r} está ${relacion} ${p.resolucionIdeal}, ${explicacion}` };
}

function juzgarPorListas(
  valor: string | null,
  ideales: string[],
  aceptables: string[],
  nombre: (v: string) => string,
  etiqueta: string,
): Juicio {
  if (!valor) return { nivel: 'desconocida', motivo: `El sitio no dice ${etiqueta}` };
  const enLista = (l: string[]) => l.some((x) => x.trim().toLowerCase() === valor);
  if (enLista(ideales)) return { nivel: 'ideal', motivo: `${nombre(valor)} es lo que quieres` };
  if (enLista(aceptables)) {
    return { nivel: 'aceptable', motivo: `${nombre(valor)} no es lo ideal, pero te vale` };
  }
  const quieres = ideales.concat(aceptables).map(nombre).join(' o ') || 'otra cosa';
  return { nivel: 'rechazada', motivo: `${nombre(valor)} no te vale: quieres ${quieres}` };
}

/** Nombre legible de un idioma, para los motivos. Cae al código si no se conoce. */
function nombreIdioma(codigo: string): string {
  return NOMBRE_IDIOMA[codigo] ?? codigo;
}

/** ¿Cuenta esta pista como «el idioma que exijo»?
 *
 *  El castellano y el latino son idiomas distintos —tienen banderas distintas en el sitio y
 *  se eligen aparte— salvo para una pregunta: si la película ES hispanohablante, cualquiera
 *  de los dos ES la voz original. Eso es lo que resuelve `mismaLengua`. */
function mismaLengua(a: string, b: string): boolean {
  const raiz = (x: string) => x.toLowerCase().split('-')[0];
  return raiz(a) === raiz(b);
}

/** El audio, que es el único requisito que no se negocia.
 *
 *  Tres cosas se juntan aquí, y en este orden:
 *
 *  1. **¿Se entiende?** Si no trae ninguno de los idiomas exigidos, no vale y punto: una
 *     película que no se entiende no se ve, por bien empaquetada que esté.
 *  2. **¿Está la voz original?** Con `exigirIdiomaOriginal`, una versión que solo trae el
 *     doblaje baja a ámbar: se puede ver, pero no como se rodó. Cuando la película ya es
 *     hispanohablante no hay nada que echar de menos y el doblaje ES el original.
 *  3. **¿Y si no se sabe cuál es el original?** El país no siempre está en la ficha (los
 *     animes vienen sin él). Entonces se mira lo que hay: traer castellano Y algún otro
 *     audio es, en la práctica, VO más doblaje, así que se da por bueno; traer solo
 *     castellano deja la duda de que falte la voz original, y eso es exactamente el ámbar.
 *     Suponer inglés por defecto marcaría en ámbar media filmoteca europea. */
function juzgarAudio(
  langs: string[],
  p: PreferenciasVersion,
  idiomaOriginal: string | null,
): Juicio {
  const suyos = langs.map((l) => l.trim().toLowerCase()).filter(Boolean);
  // Sin banderas de audio no es que no lo traiga: es que el release no las puso. Se
  // distingue de «trae audios y ninguno vale», que sí es un rechazo.
  if (!suyos.length) return { nivel: 'desconocida', motivo: 'Esta versión no declara ningún audio' };

  const exigidos = p.audioIdeal.map((l) => l.toLowerCase());
  const tiene = exigidos.filter((l) => suyos.includes(l));
  const tolerados = p.audioAceptable.map((l) => l.toLowerCase()).filter((l) => suyos.includes(l));

  if (!tiene.length && !tolerados.length) {
    return {
      nivel: 'rechazada',
      motivo: `Solo trae audio en ${suyos.map(nombreIdioma).join(', ')}: no está ${exigidos.map(nombreIdioma).join(' ni ')}`,
    };
  }

  // Se entiende, pero con un idioma de los de segunda: eso ya es un ámbar por sí solo, y el
  // original no lo puede subir.
  if (!tiene.length) {
    return {
      nivel: 'aceptable',
      motivo: `Trae ${tolerados.map(nombreIdioma).join(', ')} pero no ${exigidos.map(nombreIdioma).join(' ni ')}`,
    };
  }

  const loQueExiges = tiene.map(nombreIdioma).join(', ');
  if (!p.exigirIdiomaOriginal) {
    return { nivel: 'ideal', motivo: `Trae audio en ${loQueExiges}, que es lo que exiges` };
  }

  if (idiomaOriginal) {
    // Una película hispanohablante no tiene otra voz que echar de menos.
    if (exigidos.some((l) => mismaLengua(l, idiomaOriginal)) ||
        tolerados.some((l) => mismaLengua(l, idiomaOriginal))) {
      return {
        nivel: 'ideal',
        motivo: `Trae ${loQueExiges}, y la película se rodó en ${nombreIdioma(idiomaOriginal)}: no falta ninguna voz`,
      };
    }
    if (suyos.some((l) => mismaLengua(l, idiomaOriginal))) {
      return {
        nivel: 'ideal',
        motivo: `Trae ${loQueExiges} y el original en ${nombreIdioma(idiomaOriginal)}`,
      };
    }
    return {
      nivel: 'aceptable',
      motivo: `Trae ${loQueExiges}, pero no el original en ${nombreIdioma(idiomaOriginal)}: solo doblada`,
    };
  }

  // Sin país en la ficha: lo que hay es lo que se puede juzgar.
  const otros = suyos.filter((l) => !tiene.includes(l) && !tolerados.includes(l));
  if (otros.length) {
    return {
      nivel: 'ideal',
      motivo: `Trae ${loQueExiges} y ${otros.map(nombreIdioma).join(', ')}, que será el original —la ficha no dice de dónde es la película`,
    };
  }
  return {
    nivel: 'aceptable',
    motivo: `Solo trae ${loQueExiges}: si la película no se rodó en español, falta la voz original`,
  };
}

/** Los subtítulos, con la misma vara que el audio pero sin poder tumbar una versión.
 *
 *  Verde es lo mismo que en el audio: poder leerla en el idioma que entiendes **y** tener
 *  el del original —o que el original ya sea el tuyo—. Todo lo demás es ámbar, y **nunca
 *  rojo**: unos subtítulos que faltan se descargan aparte en dos minutos, así que no son
 *  motivo para descartar una versión que por lo demás está bien. Esa es toda la diferencia
 *  con `juzgarAudio`, donde no entender la película sí es definitivo. */
function juzgarSubtitulos(
  v: VersionValorable,
  p: PreferenciasVersion,
  idiomaOriginal: string | null,
): Juicio {
  const suyos = (v.subtitleLangs ?? []).map((l) => l.trim().toLowerCase()).filter(Boolean);
  const exigidos = p.audioIdeal.map((l) => l.toLowerCase());
  const tolerados = p.audioAceptable.map((l) => l.toLowerCase());

  if (!suyos.length) {
    return {
      nivel: 'aceptable',
      motivo: v.subsSinEspecificar
        ? 'Trae subtítulos, pero el sitio no dice de qué idioma'
        : 'No trae subtítulos: habrá que buscarlos aparte',
    };
  }

  const tuyos = suyos.filter((l) => exigidos.includes(l) || tolerados.includes(l));
  if (!tuyos.length) {
    return {
      nivel: 'aceptable',
      motivo: `Solo trae subtítulos en ${suyos.map(nombreIdioma).join(', ')}`,
    };
  }

  const losTuyos = tuyos.map(nombreIdioma).join(', ');
  if (!p.exigirIdiomaOriginal) {
    return { nivel: 'ideal', motivo: `Trae subtítulos en ${losTuyos}` };
  }

  if (idiomaOriginal) {
    if (tuyos.some((l) => mismaLengua(l, idiomaOriginal))) {
      return {
        nivel: 'ideal',
        motivo: `Trae subtítulos en ${losTuyos}, que es la lengua en la que se rodó`,
      };
    }
    if (suyos.some((l) => mismaLengua(l, idiomaOriginal))) {
      return {
        nivel: 'ideal',
        motivo: `Trae subtítulos en ${losTuyos} y en el original en ${nombreIdioma(idiomaOriginal)}`,
      };
    }
    return {
      nivel: 'aceptable',
      motivo: `Trae subtítulos en ${losTuyos}, pero no en el original en ${nombreIdioma(idiomaOriginal)}`,
    };
  }

  // Sin país en la ficha, igual que con el audio: lo que hay es lo que se puede juzgar.
  const otros = suyos.filter((l) => !tuyos.includes(l));
  return otros.length
    ? {
        nivel: 'ideal',
        motivo: `Trae subtítulos en ${losTuyos} y ${otros.map(nombreIdioma).join(', ')}, que será el original`,
      }
    : {
        nivel: 'aceptable',
        motivo: `Solo trae subtítulos en ${losTuyos}: si la película no se rodó en español, faltan los del original`,
      };
}

/** Bitrate de referencia por resolución, en Mbps de UN vídeo, para x264 y XviD.
 *
 *  No son números de catálogo: salen de medir **4237 versiones reales** de la lista de
 *  películas, con la consulta de `scripts/calibrar-bitrates.sql`. Cada banda cubre de su
 *  percentil 5 a su percentil 95, así que lo que queda fuera es la cola de verdad —las
 *  versiones a menos de 1 Mbps que se ven a bloques, o el 1080p a 36 Mbps que no se ve
 *  mejor por ello— y no la mitad del catálogo.
 *
 *  Un bitrate absoluto es lo único que se puede comparar entre fichas distintas: divide
 *  por la duración, que es la variable que más manda, y HispaShare la publica en todas las
 *  fichas (1274 de 1289 medidas). Lo que se comparaba antes contra las otras versiones de
 *  la misma ficha casi nunca llegaba a activarse: hacían falta tres versiones de la misma
 *  resolución, y lo normal es que una ficha tenga una o dos de cada. */
const BANDA_BITRATE: Record<Resolution, { min: number; max: number }> = {
  '480p': { min: 0.9, max: 3.5 },
  // Medido: p05 0,90 y p95 2,48 en 711 versiones XviD.
  '576p': { min: 1.1, max: 4.5 },
  // Interpolado: solo hay una versión 576p en la muestra.
  '720p': { min: 2.0, max: 9 },
  // Medido: 2,62–8,42 en 294 versiones x264.
  '1080p': { min: 2.5, max: 13 },
  // Medido: 2,33–12,51 en 1730 versiones x264. La mediana, 6,00, es donde está la masa.
  '2160p': { min: 9, max: 70 },
  // Ancha a la fuerza: el 4K viene en dos familias que conviven —337 «Micro4K» entre 4 y
  // 8 Mbps y 654 remuxes por encima de 15—, y ninguna de las dos es un error.
};

/** Lo que cuesta la misma calidad en cada familia de códec, medido en la misma muestra:
 *  a 1080p la mediana es 6,00 Mbps en x264, 2,97 en x265 y 3,85 en AV1.
 *
 *  XviD **no lleva penalización** aunque comprima mucho peor: sus rips vienen de DVD, así
 *  que el techo lo pone el material y no el códec. En los datos sale igual o más bajo que
 *  x264 a la misma resolución (480p: 1,84 contra 2,16), de modo que corregirlo al alza
 *  marcaría en rojo a la mitad del catálogo antiguo. */
const FACTOR_CODEC: Record<string, number> = { h265: 0.55, av1: 0.6 };

/** Cuánto se ensancha la banda antes de decidir. `normal` es la banda tal cual se midió;
 *  los otros dos la abren o la cierran entera, que es más fácil de entender que diez
 *  números sueltos. El ámbar es siempre la mitad de camino hacia afuera. */
const MARGEN_TOLERANCIA: Record<ToleranciaTamano, number> = {
  exigente: 0.85,
  normal: 1,
  tolerante: 1.3,
};

/** Cuánto se sale del verde antes de pasar a rojo. */
const ANCHO_AMBAR = 1.6;

/** Lo que ocupa UN vídeo de la versión.
 *
 *  No es `sizeBytes` del grupo: un release partido en dos `.avi` suma el doble que el
 *  mismo contenido en un fichero, y una temporada entera suma trece veces más sin que
 *  ninguna de las dos sea «más grande». Los subtítulos y los `.rar` de la banda sonora no
 *  entran. */
export function bytesPorVideo(v: VersionValorable): number | null {
  const videos = v.files.filter((f) => f.kind === 'video' && f.sizeBytes != null);
  if (!videos.length) return null;
  const total = videos.reduce((a, f) => a + (f.sizeBytes ?? 0), 0);
  return total > 0 ? Math.round(total / videos.length) : null;
}

/** Mbps de un vídeo de esta versión, o `null` si falta algún dato para saberlo.
 *
 *  `duracionMin` es la de la ficha. En una serie es la de UN episodio, y como el tamaño
 *  también se toma por fichero, la cuenta sale bien en los dos casos. */
export function bitrateMbps(v: VersionValorable, duracionMin: number | null): number | null {
  const bytes = bytesPorVideo(v);
  if (bytes == null || !duracionMin || duracionMin <= 0) return null;
  return (bytes * 8) / (duracionMin * 60) / 1e6;
}

/** La banda de esta versión, ya corregida por códec y por tolerancia. */
export function bandaDe(
  v: VersionValorable,
  p: PreferenciasVersion,
): { min: number; max: number } | null {
  const base = v.resolution ? BANDA_BITRATE[v.resolution as Resolution] : undefined;
  if (!base) return null;
  const codec = FACTOR_CODEC[familiaCodec(v.videoCodec) ?? ''] ?? 1;
  const k = MARGEN_TOLERANCIA[p.toleranciaTamano];
  return { min: (base.min * codec) / k, max: base.max * codec * k };
}

function redondear(x: number): string {
  return x >= 10 ? x.toFixed(0) : x.toFixed(1).replace('.', ',');
}

function juzgarTamano(
  v: VersionValorable,
  p: PreferenciasVersion,
  duracionMin: number | null,
): Juicio {
  if (bytesPorVideo(v) == null) {
    return { nivel: 'desconocida', motivo: 'Esta versión no trae ningún vídeo con tamaño' };
  }
  // Sin duración no hay bitrate, y sin bitrate el tamaño no dice nada: dos gigas son
  // buenos para hora y media y ridículos para tres horas.
  const mbps = bitrateMbps(v, duracionMin);
  if (mbps == null) {
    return { nivel: 'desconocida', motivo: 'La ficha no dice cuánto dura, así que el tamaño no se puede juzgar' };
  }
  const banda = bandaDe(v, p);
  if (!banda) {
    return { nivel: 'desconocida', motivo: 'Sin saber la resolución no hay con qué comparar el tamaño' };
  }

  const cuanto = `${redondear(mbps)} Mbps`;
  const esperado = `lo normal en ${v.resolution} son ${redondear(banda.min)}-${redondear(banda.max)}`;
  if (mbps >= banda.min && mbps <= banda.max) {
    return { nivel: 'ideal', motivo: `${cuanto}: dentro de lo normal para ${v.resolution}` };
  }
  if (mbps >= banda.min / ANCHO_AMBAR && mbps <= banda.max * ANCHO_AMBAR) {
    return {
      nivel: 'aceptable',
      motivo:
        mbps < banda.min
          ? `${cuanto}, algo justo: ${esperado}`
          : `${cuanto}, algo pasado: ${esperado}`,
    };
  }
  return {
    nivel: 'rechazada',
    motivo:
      mbps < banda.min
        ? `${cuanto} para ${v.resolution}: recomprimida, se verá a bloques (${esperado})`
        : `${cuanto} para ${v.resolution}: ocupa mucho más sin verse mejor (${esperado})`,
  };
}

const ORDEN_PEOR: Nivel[] = ['rechazada', 'desconocida', 'aceptable', 'ideal'];

function peor(niveles: Nivel[]): Nivel {
  return ORDEN_PEOR.find((n) => niveles.includes(n)) ?? 'desconocida';
}

/** Lo que aporta la FICHA, que es de la película y no de cada versión.
 *
 *  Sin duración el tamaño no se puede juzgar —dos gigas son buenos para hora y media y
 *  ridículos para tres horas— y sin país no se sabe qué voz es la original. Los dos campos
 *  son opcionales porque los dos faltan a veces, y faltar tiene su propia respuesta. */
export interface ContextoFicha {
  duracionMin?: number | null;
  /** Tal como los escribe el sitio: `EE.UU.`, `España`. Ver `idiomaOriginalDe`. */
  paises?: string[] | null;
}

/** Valora UNA versión, con lo que se sepa de su ficha. */
export function valorarVersion(
  v: VersionValorable,
  p: PreferenciasVersion,
  ficha: ContextoFicha = {},
): Valoracion {
  const contenedor = juzgarPorListas(
    normalizarContenedor(v.container),
    p.contenedoresIdeales,
    p.contenedoresAceptables,
    (x) => x.toUpperCase(),
    'en qué contenedor viene',
  );
  const codec = juzgarPorListas(
    familiaCodec(v.videoCodec),
    p.codecsIdeales,
    p.codecsAceptables,
    (x) => NOMBRE_FAMILIA[x] ?? x,
    'con qué códec está comprimida',
  );
  const resolucion = juzgarResolucion(v, p);
  const idiomaOriginal = idiomaOriginalDe(ficha.paises);
  const audio = juzgarAudio(v.audioLangs, p, idiomaOriginal);
  const subtitulos = juzgarSubtitulos(v, p, idiomaOriginal);
  const tamano = juzgarTamano(v, p, ficha.duracionMin ?? null);

  return componer({ resolucion, contenedor, codec, audio, subtitulos, tamano });
}

/** Los seis juicios sumados y resumidos. `desconocida` vale cero y no arrastra el conjunto. */
function componer(j: Omit<Valoracion, 'conjunto' | 'puntos'>): Valoracion {
  const puntos =
    VALOR[j.audio.nivel] * PESOS.audio +
    VALOR[j.resolucion.nivel] * PESOS.resolucion +
    VALOR[j.contenedor.nivel] * PESOS.contenedor +
    VALOR[j.codec.nivel] * PESOS.codec +
    VALOR[j.subtitulos.nivel] * PESOS.subtitulos +
    VALOR[j.tamano.nivel] * PESOS.tamano;
  return {
    ...j,
    conjunto: peor([
      j.resolucion.nivel, j.contenedor.nivel, j.codec.nivel, j.audio.nivel, j.subtitulos.nivel, j.tamano.nivel,
    ]),
    puntos,
  };
}

/** Valora un fichero que YA está en la biblioteca (D-28), con la misma vara que una versión
 *  del sitio salvo en el CONTENEDOR, que no se juzga: la preferencia de contenedor es sobre
 *  lo que se descarga —de un MKV se saca más—, y aquí el contenedor no dice nada de la
 *  calidad que hay dentro. Juzgarlo pintaba en rojo el MP4 que la propia conversión produce,
 *  y hacía que cualquier MKV del sitio «ganara» a cualquier fichero colocado. */
export function valorarFichero(
  v: VersionValorable,
  p: PreferenciasVersion,
  ficha: ContextoFicha = {},
): Valoracion {
  const base = valorarVersion(v, p, ficha);
  return componer({
    ...base,
    contenedor: {
      nivel: 'desconocida',
      motivo: 'El contenedor no se juzga en un fichero de la biblioteca: la conversión lo cambia y no dice nada de la calidad',
    },
  });
}

/** Valora todas las versiones de una ficha de una vez. Lo de la ficha —duración y país— se
 *  pasa una sola vez, porque es de la película y no de cada versión. */
export function valorarVersiones<T extends VersionValorable>(
  versiones: T[],
  p: PreferenciasVersion,
  ficha: ContextoFicha = {},
): { version: T; valoracion: Valoracion }[] {
  return versiones.map((version) => ({
    version,
    valoracion: valorarVersion(version, p, ficha),
  }));
}

// ── Comparar dos valoraciones (D-28) ─────────────────────────────────────────

export const COLUMNAS_VALORACION = [
  'resolucion',
  'contenedor',
  'codec',
  'audio',
  'subtitulos',
  'tamano',
] as const;
export type ColumnaValoracion = (typeof COLUMNAS_VALORACION)[number];

/** Qué sale de poner una versión candidata frente a la que ya se tiene.
 *
 *  - `gana`: mejor en al menos una columna y peor en ninguna. Es la única que justifica
 *    volver a descargar sin que nadie lo mire (D-22: se pregunta por intercambio, no por
 *    diferencia).
 *  - `intercambio`: mejor en una, peor en otra. Decide una persona.
 *  - `igual`: las mismas seis columnas.
 *  - `pierde`: peor en algo y mejor en nada. */
export interface ComparacionValoraciones {
  resultado: 'gana' | 'intercambio' | 'igual' | 'pierde';
  mejora: ColumnaValoracion[];
  empeora: ColumnaValoracion[];
}

/** Las columnas que hablan de lo que hay DENTRO del fichero: las que se comparan al decidir
 *  si volver a descargar una película de la biblioteca (D-28). El contenedor no dice nada de
 *  la calidad y el códec lo cambia la conversión de todas formas (D-13): que el sitio tenga
 *  un x264 no es motivo para bajar otra vez un HEVC que se ve bien. */
export const COLUMNAS_DE_CONTENIDO: readonly ColumnaValoracion[] = [
  'resolucion',
  'audio',
  'subtitulos',
  'tamano',
];

/** La candidata frente a la actual, columna a columna, con el orden de `ORDEN_PEOR`:
 *  `desconocida` cuenta por debajo de `aceptable` a propósito. Una candidata cuyo audio
 *  el sitio no publica NO gana a un fichero cuyo audio se ha leído con ffprobe, aunque
 *  tenga más resolución; eso es un intercambio, y se pregunta. Los puntos no entran: una
 *  suma compensa, y aquí lo que se quiere es saber si hay algo que se pierde. */
export function compararValoraciones(
  candidata: Valoracion,
  actual: Valoracion,
  columnas: readonly ColumnaValoracion[] = COLUMNAS_VALORACION,
): ComparacionValoraciones {
  const mejora: ColumnaValoracion[] = [];
  const empeora: ColumnaValoracion[] = [];
  for (const c of columnas) {
    const d = ORDEN_PEOR.indexOf(candidata[c].nivel) - ORDEN_PEOR.indexOf(actual[c].nivel);
    if (d > 0) mejora.push(c);
    else if (d < 0) empeora.push(c);
  }
  const resultado = mejora.length
    ? empeora.length
      ? 'intercambio'
      : 'gana'
    : empeora.length
      ? 'pierde'
      : 'igual';
  return { resultado, mejora, empeora };
}
